// Typed API surface for the landed-cost feature (G-WebB). Built from the two
// shared primitives (src/hooks/useApi.ts) over the typed fetch wrapper
// (src/api/client.ts), matching the pattern every other feature/api.ts
// establishes. Response shapes mirror the wire contracts in
// services/api/src/modules/intake/landed-cost.ts and
// packages/core/src/schemas/intake.ts; request bodies are typed directly
// against `@zebra/core`'s zod-inferred input types.
//
// This feature does NOT import from features/purchasing-intake/api.ts (that
// file is off-limits to this goal, and cross-feature imports have no precedent
// anywhere in this codebase — features/sales-invoicing/api.ts and
// features/purchasing-intake/api.ts both independently duplicate their own
// `useSites`/`useLocations`/`useProducts`-style config lookups rather than
// share one; see sales-invoicing/format.ts's header for the same reasoning
// applied to formatters). The couple of read-only lookups this screen needs
// (suppliers, products, posted goods receipts) are duplicated here, narrowed to
// only the fields this screen actually renders.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';
import { useApiMutation, useApiQuery } from '../../hooks/useApi';
import type {
  GoodsReceiptResponse,
  LandedCostDocResponse,
  LandedCostDocStatus,
  LandedCostDocWrite,
  LandedCostPostResponse,
  LandedCostReverseResponse,
} from '@zebra/core';

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

// ---------------------------------------------------------------------------
// Read-only lookups (narrowed to what this screen renders)
// ---------------------------------------------------------------------------

export interface SupplierRow {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

export function useSuppliers(params: { isActive?: boolean } = {}) {
  return useApiQuery<Page<SupplierRow>>(['landed-cost', 'suppliers', params], '/purchasing/suppliers', {
    query: { isActive: params.isActive, limit: 200 },
  });
}

export interface ProductRow {
  id: string;
  sku: string;
  name: string;
}

export function useProducts() {
  return useApiQuery<Page<ProductRow>>(['landed-cost', 'products'], '/config/products', { query: { limit: 200 } });
}

/** Only POSTED goods receipts carry a `payableCents` on their lines (§9.3's
 * allocation weight only exists once a RECEIPT movement has actually posted —
 * see landed-cost.ts's file header) — this is the exact pool of candidate
 * target lines a landed cost doc can be allocated across. */
export function usePostedGoodsReceipts() {
  return useApiQuery<Page<GoodsReceiptResponse>>(['landed-cost', 'goods-receipts', 'POSTED'], '/intake/goods-receipts', {
    query: { status: 'POSTED', limit: 200 },
  });
}

// ---------------------------------------------------------------------------
// Landed cost docs (intake module) — this feature's sole write surface.
// ---------------------------------------------------------------------------

/** Wire-safe variant of `LandedCostDocWrite` — `amountCents` is typed `bigint`
 * on the zod-INFERRED input type (`moneyCentsSchema`'s `.transform` normalizes
 * to bigint, and `z.infer` reflects that OUTPUT type), but a browser can never
 * actually SEND a raw bigint as a request body (`JSON.stringify` throws on
 * one) — mirrors purchasing-intake/api.ts's `SupplierContractWriteInput`/
 * `PurchaseOrderLineWriteInput` precedent for the identical reason. The
 * server's zod validator accepts this numeric-string form directly (the same
 * union `moneyCentsSchema` always allowed), so this is not a shape change.
 */
export type LandedCostDocWriteInput = Omit<LandedCostDocWrite, 'amountCents'> & { amountCents: string };

function invalidateLandedCostDocs(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['landed-cost', 'landed-cost-docs'] });
}

export function useLandedCostDocs(params: { status?: LandedCostDocStatus; supplierId?: string } = {}) {
  return useApiQuery<Page<LandedCostDocResponse>>(['landed-cost', 'landed-cost-docs', params], '/intake/landed-cost-docs', {
    query: { status: params.status, supplierId: params.supplierId, limit: 200 },
  });
}

export function useLandedCostDoc(id: string | undefined) {
  return useApiQuery<LandedCostDocResponse>(['landed-cost', 'landed-cost-doc', id], `/intake/landed-cost-docs/${id}`, {
    enabled: Boolean(id),
  });
}

export function useCreateLandedCostDoc() {
  const qc = useQueryClient();
  return useApiMutation<LandedCostDocResponse, LandedCostDocWriteInput>('/intake/landed-cost-docs', 'POST', {
    onSuccess: () => invalidateLandedCostDocs(qc),
  });
}

export function useUpdateLandedCostDoc(id: string) {
  const qc = useQueryClient();
  return useApiMutation<LandedCostDocResponse, LandedCostDocWriteInput>(`/intake/landed-cost-docs/${id}`, 'PATCH', {
    onSuccess: () => {
      invalidateLandedCostDocs(qc);
      qc.invalidateQueries({ queryKey: ['landed-cost', 'landed-cost-doc', id] });
    },
  });
}

export function usePostLandedCostDoc(id: string) {
  const qc = useQueryClient();
  return useApiMutation<LandedCostPostResponse, void>(`/intake/landed-cost-docs/${id}/post`, 'POST', {
    onSuccess: () => {
      invalidateLandedCostDocs(qc);
      qc.invalidateQueries({ queryKey: ['landed-cost', 'landed-cost-doc', id] });
    },
  });
}

export function useReverseLandedCostDoc(id: string) {
  const qc = useQueryClient();
  return useApiMutation<LandedCostReverseResponse, void>(`/intake/landed-cost-docs/${id}/reverse`, 'POST', {
    onSuccess: () => {
      invalidateLandedCostDocs(qc);
      qc.invalidateQueries({ queryKey: ['landed-cost', 'landed-cost-doc', id] });
    },
  });
}

// ---------------------------------------------------------------------------
// "Create & post in one step" — a composite action spanning two dedicated
// posting endpoints (create doc -> post doc), for the common case where the
// operator has already reviewed the split preview and just wants to commit it
// immediately. NOT a single `useApiMutation` (that helper is for one fixed
// path) since the post step's path depends on the id the create step just
// returned — mirrors purchasing-intake/api.ts's `useReceiveLoad`, the
// established precedent for this exact composite-mutation shape in this
// codebase.
// ---------------------------------------------------------------------------

export function useCreateAndPostLandedCostDoc() {
  const qc = useQueryClient();
  return useMutation<LandedCostPostResponse, Error, LandedCostDocWriteInput>({
    mutationFn: async (input) => {
      const created = await apiFetch<LandedCostDocResponse>('/intake/landed-cost-docs', { method: 'POST', body: input });
      return apiFetch<LandedCostPostResponse>(`/intake/landed-cost-docs/${created.id}/post`, { method: 'POST' });
    },
    onSuccess: () => invalidateLandedCostDocs(qc),
  });
}
